import { useEffect, useRef, useState } from 'react'
import { Trash2, Camera } from 'lucide-react'
import PropTypes from 'prop-types'

import { getImageThumbnail, removeAttachment, updatePhotoLabel, uploadImageAttachment } from '../../../api/api'
import { PhotoViewer } from '@components/photo'
import { OperationType } from '../../../enums/enums'
import { ConfirmModal } from '@components/common'
import { getClientInvoiceFile } from '../../../api/api'
import { GENERIC_MESSAGES, BUTTONS_DISPLAY_LABEL } from '../../../constants'
import { ATTACHMENT_TYPE_MAP, PHOTO_DISPLAY_LABEL_MAP } from '../../../variable-maps'
import {
  AdasBox,
  AdasButton,
  AdasFormControl,
  AdasInputLabel,
  AdasSelect,
  AdasTooltip,
  AdasTypography,
  AdasImage
} from '@components/wrapper-components'
import { AdasLoader } from '@components/adas-loader'
import { Logger } from '../../../logger'
import { useMessageStore } from '@caradasstore/MessageStore'

const DeleteMe = ({ whenDelete }) => {
  return (
    <AdasTooltip title='Delete'>
      <AdasButton buttonType='iconButton' size='medium' className='absolute top-2 right-2' onClick={whenDelete}>
        <Trash2 className='w-4 h-4' style={{ color: '#ef5350' }} />
      </AdasButton>
    </AdasTooltip>
  )
}

export const PhotoInput = (props) => {
  const {
    labelText,
    imageLocation,
    workorderId,
    isClientInvoice = false,
    clientAccessData,
    deleteOnRetake = false,
    onUpdate,
    allowDelete = false,
    imageId,
    label,
    labelList,
    disabled = false,
    img_rotation,
    title,
    isModalAlreadyOpened = false,
    isMultiUpload = false,
    maxPhotos,
    compact = false
  } = props

  const fileRef = useRef(null)
  const [customLabel, setCustomLabel] = useState(label ? label : '')

  const [isModalOpen, setModalOpen] = useState(isModalAlreadyOpened)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [deleteAttachmentId, setDeleteAttachment] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const addMessage = useMessageStore((store) => store.addMessage)

  useEffect(() => {
    const fetchThumbnailData = async () => {
      if (isClientInvoice) {
        return getClientInvoiceFile({
          token: clientAccessData.token,
          invoiceId: clientAccessData.invoiceId,
          attachmentId: imageId
        })
      } else {
        return getImageThumbnail({
          attachmentId: imageId
        })
      }
    }

    const fetchImageThumbnail = async () => {
      if (!imageId) {
        return
      }

      try {
        setLoading(true)
        const thumbnailData = await fetchThumbnailData()
        const url = URL.createObjectURL(thumbnailData.data)
        setImageUrl(url)
      } catch (error) {
        Logger.error({ message: error, payload: { path: 'PhotoInput/FetchImageThumbnail', imageId } })
      } finally {
        setLoading(false)
      }
    }

    fetchImageThumbnail()
  }, [imageId, isClientInvoice, clientAccessData?.token, clientAccessData?.invoiceId])

  const openModal = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const handleUpload = async (ref) => {
    let files = isMultiUpload ? Array.from(ref.current.files) : [ref.current.files[0]]
    if (files.length > maxPhotos) {
      files = files.slice(0, maxPhotos)
      addMessage({ text: `Exceeded max upload limit! Uploading only first ${maxPhotos} ${maxPhotos > 1 ? 'images' : 'image'}` })
    }
    for (const file of files) {
      setLoading(true)
      const formData = new FormData()
      formData.append('file', file, file.name)
      let attachmentType = ATTACHMENT_TYPE_MAP[imageLocation]
      const params = {
        data: formData,
        workorderId: workorderId,
        type: attachmentType
      }
      try {
        if (deleteOnRetake && imageId) {
          await removeAttachment({ attachmentId: imageId })
        }

        const response = await uploadImageAttachment(params)

        if (response?.data) {
          const updatedData = {
            operation: OperationType.RETAKE,
            attachmentId: response.data?.id,
            img_rotation: 0,
            imageLocation: imageLocation,
            attachmentDetail: response.data
          }
          onUpdate(updatedData)
        }
      } catch (error) {
        console.error('Error uploading image:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const deleteAttachment = async (attachmentId) => {
    await removeAttachment({ attachmentId: attachmentId })

    const updatedData = {
      operation: OperationType.DELETE,
      imageLocation: imageLocation,
      attachmentId: attachmentId
    }
    onUpdate(updatedData)
  }

  const handleDelete = async () => {
    if (deleteAttachmentId) {
      setShowConfirmModal(false)
      deleteAttachment(deleteAttachmentId)
    }
  }

  const deleteWithConfirmation = (deleteImageId) => {
    setDeleteAttachment(deleteImageId)
    setShowConfirmModal(true)
  }

  const photoLabelHandler = async (value) => {
    const params = {
      attachmentId: imageId,
      label: value ? value : ''
    }

    const updateLabelResponse = await updatePhotoLabel(params)
    if (updateLabelResponse.status === 200) {
      setCustomLabel(value ? value : '')
    }
  }

  const classes = {
    subHeading: {
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '21.98px',
      letterSpacing: '0.1px',
      marginTop: '8px',
      padding: '4px 8px',
      color: 'rgba(26, 27, 31, 0.87)'
    }
  }

  // CAR-3850: on a phone the label wraps and outgrows the 4:3 tile, and a grid (or flex) container
  // won't stretch an item that has an aspect ratio by default — so tiles in one row came out
  // different heights. self-stretch opts back in: the tallest tile sets the row and the others
  // match it, while aspect-[4/3] still sizes a tile with nothing beside it. A tile wrapped in its
  // own box needs that box to be `flex` for this to reach it.
  return !loading && !imageId ? (
    compact ? (
      <label
        className={`flex aspect-[4/3] w-full flex-col self-stretch items-center justify-center rounded-xl border border-dashed border-black/[0.12] bg-[#F1F4F8] px-4 py-6 text-center transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#E8EDF3]'
        }`}
      >
        <Camera className='h-7 w-7 text-[#6B7280]' strokeWidth={1.75} />
        <div className='mt-2 text-[14px] font-medium text-[#6B7280]'>Add Photo</div>
        <input
          ref={fileRef}
          type='file'
          hidden
          multiple={isMultiUpload}
          accept='image/*'
          onChange={() => handleUpload(fileRef)}
          disabled={disabled}
        />
      </label>
    ) : (
      <label
        className={`mb-[20px] flex aspect-[4/3] w-full flex-col self-stretch items-center justify-center rounded-xl border border-dashed border-black/[0.12] bg-[#F1F4F8] px-4 py-7 text-center transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#E8EDF3]'
        }`}
      >
        <Camera className='h-7 w-7 text-[#0A1628]' strokeWidth={1.75} />
        <div className='mt-2 text-[14px] font-bold text-[#0A1628]'>Tap to capture</div>
        <div className='mt-0.5 text-[13px] normal-case text-[#6B7280]'>{labelText}</div>
        <div className='mt-1 text-[13px] font-medium text-[#1F6FEB] underline underline-offset-2'>Upload from gallery</div>
        <input
          ref={fileRef}
          type='file'
          hidden
          multiple={isMultiUpload}
          accept='image/*'
          onChange={() => handleUpload(fileRef)}
          disabled={disabled}
        />
      </label>
    )
  ) : (
    <AdasBox className='flex w-full flex-col items-center'>
      <AdasBox id='image' className='relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-[#F1F4F8]'>
        {loading ? (
          <AdasLoader className='aspect-[4/3] w-full rounded-xl' />
        ) : (
          <AdasImage
            src={imageUrl}
            alt={PHOTO_DISPLAY_LABEL_MAP[imageLocation]}
            className='h-full w-full object-contain'
            style={{ transform: `rotate(${img_rotation}deg)` }}
          />
        )}
        {allowDelete && !disabled && imageId && <DeleteMe whenDelete={() => deleteWithConfirmation(imageId)} />}
        {!isClientInvoice && (
          <>
            <button
              type='button'
              onClick={openModal}
              disabled={loading}
              className='absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-[#0A1628] to-[#0A1628]/0 py-2.5 text-[12px] font-bold uppercase tracking-wide text-white'
            >
              <Camera className='h-4 w-4' />
              View Photo
            </button>
            <PhotoViewer
              isOpen={isModalOpen}
              closeModal={closeModal}
              imageUrl={imageUrl}
              workorderId={workorderId}
              imageId={imageId}
              imageLocation={imageLocation}
              img_rotation={img_rotation}
              onUpdate={onUpdate}
              allowDelete={allowDelete}
              disabled={disabled}
              title={PHOTO_DISPLAY_LABEL_MAP[imageLocation] || title}
            />
          </>
        )}
      </AdasBox>
      {showConfirmModal && (
        <ConfirmModal
          cancelLabel={BUTTONS_DISPLAY_LABEL.CANCEL}
          confirmLabel={BUTTONS_DISPLAY_LABEL.DELETE}
          open={showConfirmModal}
          title={GENERIC_MESSAGES.DELETE_PHOTO}
          message={GENERIC_MESSAGES.CONFIRM_DELETE_PHOTO}
          handleClose={() => setShowConfirmModal(false)}
          handleContinue={handleDelete}
        />
      )}
      {imageLocation !== 'additionalPhotos' ? (
        <AdasTypography
          variant='subtitle2'
          className='font-normal text-sm leading-[21.98px] tracking-[0.1px] mt-2 py-1 px-2 text-[rgba(26,27,31,0.87)]'
        >
          {PHOTO_DISPLAY_LABEL_MAP[imageLocation]}
        </AdasTypography>
      ) : !isClientInvoice ? (
        <AdasFormControl fullWidth className='mt-[15px] w-[90%]'>
          <AdasInputLabel id='Select Label'>Select Label</AdasInputLabel>
          <AdasSelect
            label='Select Label'
            className='w-full overflow-y-auto max-h-[100px] rounded-tl-[4px] rounded-tr-none rounded-bl-[4px] rounded-br-none'
            onChange={(e) => photoLabelHandler(e.target.value)}
            value={customLabel}
            name='labels'
            disabled={loading || disabled}
            options={labelList || []}
          />
        </AdasFormControl>
      ) : (
        <AdasTypography
          variant='subtitle2'
          className='font-normal text-sm leading-[21.98px] tracking-[0.1px] mt-2 py-1 px-2 text-[rgba(26,27,31,0.87)]'
        >
          {label}
        </AdasTypography>
      )}
    </AdasBox>
  )
}

PhotoInput.propTypes = {
  imageUrl: PropTypes.string,
  labelText: PropTypes.string,
  imageLocation: PropTypes.string,
  workorderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isClientInvoice: PropTypes.bool,
  deleteOnRetake: PropTypes.bool,
  onUpdate: PropTypes.func,
  allowDelete: PropTypes.bool,
  imageId: PropTypes.string,
  label: PropTypes.string,
  labelList: PropTypes.array,
  disabled: PropTypes.bool,
  img_rotation: PropTypes.number,
  title: PropTypes.string,
  isModalAlreadyOpened: PropTypes.bool,
  compact: PropTypes.bool
}
